/**
 * Well-known values of `KeyboardEvent.key`.
 *
 * Use these instead of raw string literals to avoid typos and to make
 * keyboard-handling code self-documenting.
 *
 * Reference: https://developer.mozilla.org/en-US/docs/Web/API/UI_Events/Keyboard_event_key_values
 */
export enum EKeyboardKey {
  // ── Modifier keys ──────────────────────────────────────────────────────────
  Alt = 'Alt', // Alt (Windows / Linux) or Option (macOS)
  Control = 'Control',
  Meta = 'Meta', // Windows key (Windows) or Command/⌘ (macOS)
  Shift = 'Shift',

  // ── Editing keys ───────────────────────────────────────────────────────────
  Backspace = 'Backspace',
  Delete = 'Delete',
  Insert = 'Insert',

  // ── Navigation keys ────────────────────────────────────────────────────────
  ArrowDown = 'ArrowDown',
  ArrowLeft = 'ArrowLeft',
  ArrowRight = 'ArrowRight',
  ArrowUp = 'ArrowUp',
  End = 'End',
  Home = 'Home',
  PageDown = 'PageDown',
  PageUp = 'PageUp',

  // ── UI keys ────────────────────────────────────────────────────────────────
  Enter = 'Enter',
  Escape = 'Escape',
  Space = ' ',
  Tab = 'Tab',

  // ── Function keys ──────────────────────────────────────────────────────────
  F1 = 'F1',
  F2 = 'F2',
  F3 = 'F3',
  F4 = 'F4',
  F5 = 'F5',
  F6 = 'F6',
  F7 = 'F7',
  F8 = 'F8',
  F9 = 'F9',
  F10 = 'F10',
  F11 = 'F11',
  F12 = 'F12',

  // ── Letter keys ────────────────────────────────────────────────────────────
  // Uppercase — matches event.key when Shift is held; conventional display form
  // for keyboard shortcut labels (e.g. Ctrl+C).
  UpperA = 'A', UpperB = 'B', UpperC = 'C', UpperD = 'D', UpperE = 'E',
  UpperF = 'F', UpperG = 'G', UpperH = 'H', UpperI = 'I', UpperJ = 'J',
  UpperK = 'K', UpperL = 'L', UpperM = 'M', UpperN = 'N', UpperO = 'O',
  UpperP = 'P', UpperQ = 'Q', UpperR = 'R', UpperS = 'S', UpperT = 'T',
  UpperU = 'U', UpperV = 'V', UpperW = 'W', UpperX = 'X', UpperY = 'Y',
  UpperZ = 'Z',
  // Lowercase — matches event.key for an unshifted letter press.
  LowerA = 'a', LowerB = 'b', LowerC = 'c', LowerD = 'd', LowerE = 'e',
  LowerF = 'f', LowerG = 'g', LowerH = 'h', LowerI = 'i', LowerJ = 'j',
  LowerK = 'k', LowerL = 'l', LowerM = 'm', LowerN = 'n', LowerO = 'o',
  LowerP = 'p', LowerQ = 'q', LowerR = 'r', LowerS = 's', LowerT = 't',
  LowerU = 'u', LowerV = 'v', LowerW = 'w', LowerX = 'x', LowerY = 'y',
  LowerZ = 'z',

  // ── Digit keys (matches event.key for the number row) ──────────────────────
  Digit0 = '0', Digit1 = '1', Digit2 = '2', Digit3 = '3', Digit4 = '4',
  Digit5 = '5', Digit6 = '6', Digit7 = '7', Digit8 = '8', Digit9 = '9',
}
